<script setup lang="ts">
import { countries } from '~/assets/data/countries';
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
import { toTypedSchema } from "@vee-validate/zod";
import * as zod from 'zod';
import {
  activityCredibilityRatingsFields,
  ActivityCredibilityRatingsOptions,
  activityWhatDidYouLikeFields,
  ActivityWhatDidYouLikeOptions,
  CommitmentToChangeOptions,
  commitmentToChangeFields,
  options,
  teacherRatingsFields,
  TeacherRatingsOptions, validationOptions,
} from "~/enums/certificates/cme-outfitters.enums";

const { dispatchMessage } = useSystemMessages();
const { submitClaimCertificateForm, startDownload } = useCertificate();
const { course } = useCourseState();
const { user } = useUserStore();

const loading = ref(false);

const emit = defineEmits<{
  (e: 'downloaded'): void;
}>();

const op = ref();
const toggle = (event: Event) => {
  op.value.toggle(event);
};

/*
 * schema
 */
const schema = toTypedSchema(
  zod.object({

    /**
     * PERSONAL INFORMATION
     */
    firstName: zod.string({ message: 'This field is required' }).min(1, 'This field is required'),
    lastName: zod.string({ message: 'This field is required' }).min(1, 'This field is required'),

    userIsDoctorOfMedicine: zod.boolean({ message: 'This field is required' }),
    accreditationBody: zod.string({ message: 'This field is required' })
      .refine(val => options.accreditationBody.map(item => item.value).includes(val), {
        message: "Invalid value",
      }),

    country: zod.object({
      isoCode: zod.string({ message: 'Country is required' })
        .refine(countryIsoCode => countries.map(country => country.code).includes(countryIsoCode), {
          message: "This field is required",
        }),
      stateName: zod.string().optional(),
    }).refine(country => {
      if (country.isoCode === 'US' && (!country.stateName || country.stateName.trim() === '')) {
        return false;
      }
      return true;
    }, {
      message: "State name is required for US",
      path: ['stateName'],
    }),

    /**
     * COMMITMENT TO CHANGE
     */
    commitmentToChange: zod.object({
      options: zod.object({
        [CommitmentToChangeOptions.RegularlyAssessCOVID19Outpatients]: zod.boolean(),
        [CommitmentToChangeOptions.UseDrugInteractionCheckers]: zod.boolean(),
        [CommitmentToChangeOptions.ApplyEvidenceBasedGuidelines]: zod.boolean(),
        [CommitmentToChangeOptions.EngageWithPharmacistsNursesAndInfectiousDiseaseSpecialists]: zod.boolean(),
        [CommitmentToChangeOptions.UseReferenceTools]: zod.boolean(),
        [CommitmentToChangeOptions.NoPlanToMakeChanges]: zod.boolean(),
        [CommitmentToChangeOptions.Other]: zod.boolean(),
      }).refine(commitmentToChangeOptions => {
        return Object.values(commitmentToChangeOptions).some(value => value === true);
      },
      { message: "At least one option must be selected" },
      ),
      otherText: zod.string().optional(),
    }).refine(commitmentToChange => {
      if (commitmentToChange.options[CommitmentToChangeOptions.Other] && commitmentToChange.otherText?.trim() === '') {
        return false;
      }
      return true;
    }, {
      message: "This field is required if 'Other' is selected",
      path: ['otherText'],
    }),

    /**
     * OBJECTIVES
     */
    learningGoalsRatings: zod.array(
      zod.object({
        title: zod.string().min(1),
        rating: zod.number()
          .min(1, 'Rating must be at least 1')
          .max(5, 'Rating must be at most 5'),
      }),
    ),

    /**
     * FACULTY
     */
    teacherRatings: zod.array(
      zod.object({
        name: zod.string().min(1),
        [TeacherRatingsOptions.TeachingStrategies]: zod.number()
          .min(1, 'Rating must be at least 1')
          .max(5, 'Rating must be at most 5'),
        [TeacherRatingsOptions.Expertise]: zod.number()
          .min(1, 'Rating must be at least 1')
          .max(5, 'Rating must be at most 5'),
        [TeacherRatingsOptions.Engaging]: zod.number()
          .min(1, 'Rating must be at least 1')
          .max(5, 'Rating must be at most 5'),
        [TeacherRatingsOptions.ContentSelection]: zod.number()
          .min(1, 'Rating must be at least 1')
          .max(5, 'Rating must be at most 5'),
      }),
    ),

    /**
     * CREDIBILITY
     */
    activityCredibilityRatings: zod.object({
      [ActivityCredibilityRatingsOptions.ChangePracticeBasedOnActivity]: zod.string()
        .refine(val => options.yesOrNo.includes(val), {
          message: "Invalid value",
        }),
      [ActivityCredibilityRatingsOptions.ClinicallyRelevant]: zod.string()
        .refine(val => options.yesOrNo.includes(val), {
          message: "Invalid value",
        }),
      [ActivityCredibilityRatingsOptions.BalancedAndObjective]: zod.string()
        .refine(val => options.yesOrNo.includes(val), {
          message: "Invalid value",
        }),
      [ActivityCredibilityRatingsOptions.FreeOfCommercialBias]: zod.string()
        .refine(val => options.yesOrNo.includes(val), {
          message: "Invalid value",
        }),
      [ActivityCredibilityRatingsOptions.PercentageOfNewInformation]: zod.string()
        .refine(val => options.percentageOfNewInformation.includes(val), {
          message: "Invalid value",
        }),
    }),

    activityWhatDidYouLike: zod.object({
      [ActivityWhatDidYouLikeOptions.IncreasedKnowledge]: zod.boolean(),
      [ActivityWhatDidYouLikeOptions.ImproveCommunicationWithHealthcareTeam]: zod.boolean(),
      [ActivityWhatDidYouLikeOptions.InformationToReviewLater]: zod.boolean(),
      [ActivityWhatDidYouLikeOptions.ApplyInformationOnPractice]: zod.boolean(),
      [ActivityWhatDidYouLikeOptions.FacilitatedValuesOrEthicsToInterprofessionalPractice]: zod.boolean(),
    }).refine(data =>
      Object.values(data).some(value => value === true),
    { message: "At least one option must be selected" },
    ),

    activityMostUsefulInformation: zod.string({ message: 'This field is required' }).min(1, 'This field is required'),

    /**
     * GENERAL
     */
    suggestedTopicsForFuture: zod.string({ message: 'This field is required' }).min(1, 'This field is required'),
    generalCommentsAndSuggestions: zod.string({ message: 'This field is required' }).min(1, 'This field is required'),

    userAgreedToDataSharing: zod.boolean({ message: 'This field is required' })
      .refine(data => data,
        { message: "This field is required" },
      ),

    courseRating: zod.number({ message: 'This field is required' })
      .min(1, 'Rating must be at least 1')
      .max(5, 'Rating must be at most 5'),
  }),
);

const { defineField, errors, handleSubmit } = useForm({
  validationSchema: schema,
  initialValues: {
    firstName: user.value?.firstName || '',
    lastName: user.value?.lastName || '',

    country: {
      isoCode: '',
    },

    learningGoalsRatings: course.value?.learningGoals.map(goal => ({
      title: goal,
      rating: 0,
    })),

    teacherRatings: course.value?.teachers.map(teacher => ({
      name: teacher.name,
      [TeacherRatingsOptions.TeachingStrategies]: 0,
      [TeacherRatingsOptions.Expertise]: 0,
      [TeacherRatingsOptions.Engaging]: 0,
      [TeacherRatingsOptions.ContentSelection]: 0,
    })),

    activityCredibilityRatings: {
      [ActivityCredibilityRatingsOptions.ChangePracticeBasedOnActivity]: '',
      [ActivityCredibilityRatingsOptions.ClinicallyRelevant]: '',
      [ActivityCredibilityRatingsOptions.BalancedAndObjective]: '',
      [ActivityCredibilityRatingsOptions.FreeOfCommercialBias]: '',
      [ActivityCredibilityRatingsOptions.PercentageOfNewInformation]: '',
    },

    activityWhatDidYouLike: {
      [ActivityWhatDidYouLikeOptions.IncreasedKnowledge]: false,
      [ActivityWhatDidYouLikeOptions.ImproveCommunicationWithHealthcareTeam]: false,
      [ActivityWhatDidYouLikeOptions.InformationToReviewLater]: false,
      [ActivityWhatDidYouLikeOptions.ApplyInformationOnPractice]: false,
      [ActivityWhatDidYouLikeOptions.FacilitatedValuesOrEthicsToInterprofessionalPractice]: false,
    },

    commitmentToChange: {
      options: {
        [CommitmentToChangeOptions.RegularlyAssessCOVID19Outpatients]: false,
        [CommitmentToChangeOptions.UseDrugInteractionCheckers]: false,
        [CommitmentToChangeOptions.ApplyEvidenceBasedGuidelines]: false,
        [CommitmentToChangeOptions.EngageWithPharmacistsNursesAndInfectiousDiseaseSpecialists]: false,
        [CommitmentToChangeOptions.UseReferenceTools]: false,
        [CommitmentToChangeOptions.NoPlanToMakeChanges]: false,
        [CommitmentToChangeOptions.Other]: false,
      },
      otherText: '',
    },
  },
});

/**
 * PERSONAL INFORMATION
 */
const [firstName, firstNameProps] = defineField("firstName", validationOptions);
const [lastName, lastNameProps] = defineField("lastName", validationOptions);

const [userIsDoctorOfMedicine, userIsDoctorOfMedicineProps] = defineField("userIsDoctorOfMedicine", validationOptions);
const [accreditationBody, accreditationBodyProps] = defineField("accreditationBody", validationOptions);

const [countryIsoCode, countryIsoCodeProps] = defineField("country.isoCode", validationOptions);
const [countryStateName, countryStateNameProps] = defineField("country.stateName", validationOptions);

/**
 * COMMITMENT TO CHANGE
 */
const [commitmentToChangeOptions, commitmentToChangeOptionsProps] = defineField("commitmentToChange.options", validationOptions);
const [commitmentToChangeOtherText, commitmentToChangeOtherTextProps] = defineField("commitmentToChange.otherText", validationOptions);

/**
 * OBJECTIVES
 */
const [learningGoalsRatings] = defineField('learningGoalsRatings', validationOptions);

/**
 * FACULTY
 */
const [teacherRatings] = defineField('teacherRatings', validationOptions);

/**
 * CREDIBILITY
 */
const [activityCredibilityRatings] = defineField('activityCredibilityRatings', validationOptions);
const [activityWhatDidYouLike, activityWhatDidYouLikeProps] = defineField("activityWhatDidYouLike", validationOptions);
const [activityMostUsefulInformation, activityMostUsefulInformationProps] = defineField("activityMostUsefulInformation", validationOptions);

/**
 * GENERAL
 */
const [suggestedTopicsForFuture, suggestedTopicsForFutureProps] = defineField("suggestedTopicsForFuture", validationOptions);
const [generalCommentsAndSuggestions, generalCommentsAndSuggestionsProps] = defineField("generalCommentsAndSuggestions", validationOptions);

const [userAgreedToDataSharing, userAgreedToDataSharingProps] = defineField("userAgreedToDataSharing", validationOptions);

const [courseRating, courseRatingProps] = defineField("courseRating", validationOptions);

/*
 * submit
 */
const onSubmit = handleSubmit(async values => {
  try {
    loading.value = true;

    if (!course.value?.id) {
      return;
    }

    const { country, commitmentToChange, ...restValues } = values;

    const selectedCommitments = Object.keys(commitmentToChange.options)
      .filter(key => commitmentToChange.options[key as CommitmentToChangeOptions])
      .map(key => {
        if (key === CommitmentToChangeOptions.Other && commitmentToChange.otherText) {
          return commitmentToChange.otherText;
        }

        const option = commitmentToChangeFields.find(option => option.value === key);
        return option ? option.label : null;
      });

    const body = {
      certificateType: 'cme_certificate',
      ...restValues,
      commitmentToChange: selectedCommitments,
      countryIsoCode: country.isoCode,
      countryStateName: country.stateName,

      // filter selected statements and merge them into an array of strings
      activityWhatDidYouLike: activityWhatDidYouLikeFields.filter(field =>
        Object.keys(restValues.activityWhatDidYouLike)
        // fixme: shit code
          .filter(key => key in restValues.activityWhatDidYouLike ? restValues.activityWhatDidYouLike[key as
              ActivityWhatDidYouLikeOptions.IncreasedKnowledge ||
              ActivityWhatDidYouLikeOptions.ImproveCommunicationWithHealthcareTeam ||
              ActivityWhatDidYouLikeOptions.InformationToReviewLater ||
              ActivityWhatDidYouLikeOptions.ApplyInformationOnPractice ||
              ActivityWhatDidYouLikeOptions.FacilitatedValuesOrEthicsToInterprofessionalPractice
          ] : false)
          .includes(field.value),
      ).map(item => item.label),
    };

    const response = await submitClaimCertificateForm(course.value.id, body);

    startDownload(response.data.value as BlobPart, `CME Certificate - ${course.value?.courseTitle}.pdf`);
    dispatchMessage('Success!', 'success', "You have claimed the certificate!");
    if (course.value){
      course.value.certificateStatus = 'pro-claimed';
    }
    emit('downloaded');

  } catch (error) {
    interface ClaimCertificateError {
      data: {
        message: string;
      };
    }

    dispatchMessage('Error!', 'error', (error as ClaimCertificateError)?.data?.message || 'Something went wrong. Please try again later.');

  } finally {
    loading.value = false;
  }
});

const handleCommitmentSelection = (selectedCommitment: CommitmentToChangeOptions) => {
  console.log('selectedCommitment', selectedCommitment);

  const valueOfCommitmentToChange = commitmentToChangeOptions.value ?? {};

  if (selectedCommitment && selectedCommitment === CommitmentToChangeOptions.NoPlanToMakeChanges) {
    Object.keys(valueOfCommitmentToChange).forEach(key => {
      if (key !== CommitmentToChangeOptions.NoPlanToMakeChanges) {
        valueOfCommitmentToChange[key as CommitmentToChangeOptions] = false;
      }
    });
  }

  if (valueOfCommitmentToChange && selectedCommitment !== CommitmentToChangeOptions.NoPlanToMakeChanges) {
    valueOfCommitmentToChange[CommitmentToChangeOptions.NoPlanToMakeChanges] = false;
  }

  if (!valueOfCommitmentToChange[CommitmentToChangeOptions.Other]) {
    commitmentToChangeOtherText.value = '';
  }

  commitmentToChangeOptions.value = valueOfCommitmentToChange;
};

</script>

<template>
  <form
    class="flex flex-col gap-12"
    @submit="onSubmit"
  >
    <Card
      class="rounded-2xl p-0 text-neutral-90 shadow-medmastery"
      :pt="{
        body: {
          class: 'p-6'
        },
        content: {
          class: 'p-0'
        }
      }"
    >
      <template #content>
        <div class="text-16 text-neutral-90 leading-150">
          By submitting this form, you will complete the <strong>{{course?.courseTitle}}</strong> and claim the associated CME credits. The date on the certificate will be the date of submission of this form.
        </div>
      </template>
    </Card>

    <!-- PROFESSIONAL INFORMATION -->
    <div class="flex flex-col gap-4">
      <Divider
        align="left"
        type="solid"
        class="m-0 mb-2"
        :pt="{
          content: {
            class: 'bg-white'
          }
        }"
      >
        <span class="text-neutral-60 text-14 font-bold uppercase">Personal Information</span>
      </Divider>

      <div class="grid grid-cols-2 gap-x-6">
        <div class="flex flex-col">
          <FormFieldLabel for="firstName">
            First name *
          </FormFieldLabel>

          <InputText
            id="firstName"
            v-model="firstName"
            :invalid="!!errors.firstName"
            :disabled="loading"
            aria-describedby="firstName-help"
            @blur="firstNameProps.onBlur"
          />

          <FormFieldError id="firstName-help">
            {{errors.firstName}}
          </FormFieldError>
        </div>

        <div class="flex flex-col">
          <FormFieldLabel for="lastName">
            Last name *
          </FormFieldLabel>

          <InputText
            id="lastName"
            v-model="lastName"
            :invalid="!!errors.lastName"
            :disabled="loading"
            aria-describedby="lastName-help"
            @blur="lastNameProps.onBlur"
          />

          <FormFieldError id="lastName-help">
            {{errors.lastName}}
          </FormFieldError>
        </div>
      </div>

      <div class="flex flex-col">
        <FormFieldLabel for="userIsDoctorOfMedicine">
          Are you a medical doctor (MD, DO, or equivalent) *
        </FormFieldLabel>

        <Dropdown
          id="userIsDoctorOfMedicine"
          v-model="userIsDoctorOfMedicine"
          placeholder="Please select"
          aria-describedby="userIsDoctorOfMedicine-help"
          option-label="label"
          option-value="value"
          :invalid="!!errors.userIsDoctorOfMedicine"
          :disabled="loading"
          :options="options.userIsDoctorOfMedicine"
          @update:model-value="userIsDoctorOfMedicineProps.onBlur"
        />

        <FormFieldError id="userIsDoctorOfMedicine-help">
          {{errors.userIsDoctorOfMedicine}}
        </FormFieldError>
      </div>

      <div class="flex flex-col">
        <div class="flex items-center justify-between">
          <FormFieldLabel for="accreditationBody">
            Which type of CME credits do you want to claim? *
          </FormFieldLabel>

          <div
            class="flex items-center gap-1 text-blue-60 cursor-pointer"
            @click="toggle"
          >
            <span class="text-14 font-bold sm:block hidden">More Info</span>
            <FontAwesomeIcon
              icon="fa-circle-info"
              class="!w-4 !h-4"
            />
          </div>
          <OverlayPanel ref="op">
            <div class="max-w-[300px]">
              <div class="font-bold mb-2">
                European CME Credits
              </div>
              <div class=" text-14 text-neutral-60">
                Accredited by EACCME, these credits are recognized within Europe and support the continuing professional development of healthcare professionals in European countries.
              </div>

              <Divider />

              <div class="font-bold mb-2">
                AMA PRA Category 1 Credits
              </div>
              <div class="text-14 text-neutral-60">
                Accredited by ACCME, these credits are recognized in the United States and internationally, particularly for professionals working outside Europe.
              </div>
            </div>
          </OverlayPanel>
        </div>

        <Dropdown
          id="accreditationBody"
          v-model="accreditationBody"
          placeholder="Please select"
          option-label="label"
          option-value="value"
          :invalid="!!errors.accreditationBody"
          :disabled="loading"
          :options="options.accreditationBody"
          aria-describedby="accreditationBody-help"
          @update:model-value="accreditationBodyProps.onBlur"
        />

        <FormFieldError id="accreditationBody-help">
          {{errors.accreditationBody}}
        </FormFieldError>
      </div>

      <div class="grid grid-cols-2 gap-x-6">
        <div class="flex flex-col">
          <FormFieldLabel for="countryIsoCode">
            In which country are you located? *
          </FormFieldLabel>

          <Dropdown
            id="countryIsoCode"
            v-model="countryIsoCode"
            placeholder="Please select"
            option-label="name"
            option-value="code"
            editable
            aria-describedby="countryIsoCode-help"
            :invalid="!!errors['country.isoCode']"
            :disabled="loading"
            :options="countries"
            :show-clear="!!countryIsoCode"
            append-to="self"
            @update:model-value="countryIsoCodeProps.onBlur"
          >
            <template
              v-if="countryIsoCode"
              #value="{value}"
            >
              <div class="flex items-center">
                <MIcon
                  sprite-location="/icons/country_flags.svg"
                  :icon="value"
                  class="rounded-sm mr-2 country-flag-icon--value"
                />

                <div>{{countries.find((item) => item.code === value)?.name}}</div>
              </div>
            </template>

            <template #option="{option}">
              <div class="flex items-center">
                <MIcon
                  sprite-location="/icons/country_flags.svg"
                  :icon="option.code"
                  class="rounded-sm mr-2 country-flag-icon--option"
                />

                <div class="text-wrap">
                  {{option.name}}
                </div>
              </div>
            </template>
          </Dropdown>

          <FormFieldError id="countryIsoCode-help">
            {{errors['country.isoCode']}}
          </FormFieldError>
        </div>

        <div class="flex flex-col">
          <FormFieldLabel for="countryStateName">
            In which State do you live? (required for US citizens)
          </FormFieldLabel>

          <InputText
            id="countryStateName"
            v-model="countryStateName"
            :invalid="!!errors['country.stateName']"
            :disabled="loading"
            aria-describedby="countryStateName-help"
            @blur="countryStateNameProps.onBlur"
          />

          <FormFieldError id="countryStateName-help">
            {{errors['country.stateName']}}
          </FormFieldError>
        </div>
      </div>
    </div>

    <!-- COMMITMENT TO CHANGE -->
    <div class="flex flex-col gap-6">
      <Divider
        align="left"
        type="solid"
        class="m-0"
        :pt="{
          content: {
            class: 'bg-white'
          }
        }"
      >
        <span class="text-neutral-60 text-14 font-bold uppercase">Commitment to Change</span>
      </Divider>

      <div class="flex flex-col gap-2">
        <FormFieldLabel for="commitmentToChangeOptions">
          As a result of my participation in this activity, I will commit to the following change(s) in my practice: (Choose all that apply)
        </FormFieldLabel>

        <div class="flex flex-col gap-2">
          <label
            v-for="(option) in commitmentToChangeFields"
            :key="`commitmentToChangeOptions-${option.label}`"
            class="flex items-center gap-2"
          >
            <Checkbox
              v-if="commitmentToChangeOptions && option.value in commitmentToChangeOptions"
              v-model="commitmentToChangeOptions[option.value]"
              binary
              :invalid="!!errors['commitmentToChange.options']"
              @update:model-value="commitmentToChangeOptionsProps.onBlur"
              @change="handleCommitmentSelection(option.value)"
            />
            <span>
              {{option.label}}
            </span>
          </label>
        </div>

        <FormFieldError id="commitmentToChange-help">
          {{errors['commitmentToChange.options']}}
        </FormFieldError>

        <div
          v-if="commitmentToChangeOptions[CommitmentToChangeOptions.Other]"
          class="flex flex-col gap-2"
        >
          <InputText
            id="commitmentToChangeOtherText"
            v-model="commitmentToChangeOtherText"
            placeholder="Please describe the change(s) you plan to make"
            :disabled="loading"
            :invalid="!!errors['commitmentToChange.otherText']"
            aria-describedby="commitmentToChangeOtherText-help"
            @update:model-value="commitmentToChangeOtherTextProps.onBlur"
          />

          <FormFieldError id="commitmentToChangeOtherText-help">
            {{errors['commitmentToChange.otherText']}}
          </FormFieldError>
        </div>
      </div>
    </div>

    <!-- OBJECTIVES-->
    <div class="flex flex-col gap-6">
      <Divider
        align="left"
        type="solid"
        class="m-0"
        :pt="{
          content: {
            class: 'bg-white'
          }
        }"
      >
        <span class="text-neutral-60 text-14 font-bold uppercase">Objectives</span>
      </Divider>

      <div class="text-18 font-bold text-neutral-90">
        To what extent do you agree that the following learning objectives were met?
      </div>

      <div
        v-for="(learningGoal, index) in course?.learningGoals"
        :key="learningGoal"
      >
        <RatingScale
          v-if="learningGoalsRatings?.[index]"
          v-model="learningGoalsRatings[index].rating"
          :title="learningGoal"
          label-left="Strongly disagree"
          label-right="Strongly agree"
          :invalid="!!errors?.learningGoalsRatings && !learningGoalsRatings?.[index].rating"
        />
      </div>
    </div>

    <!-- FACULTY -->
    <div class="flex flex-col gap-6">
      <Divider
        align="left"
        type="solid"
        class="m-0"
        :pt="{
          content: {
            class: 'bg-white'
          }
        }"
      >
        <span class="text-neutral-60 text-14 font-bold uppercase">Faculty</span>
      </Divider>

      <template
        v-for="(teacher, index) in course?.teachers"
        :key="teacher.name"
      >
        <template
          v-for="(field) in teacherRatingsFields"
          :key="field"
        >
          <RatingScale
            v-if="teacherRatings?.[index]"
            v-model="teacherRatings[index][field.value]"
            :title="field.title(teacher.name)"
            :invalid="!!errors.teacherRatings && !teacherRatings[index][field.value]"
            label-left="Poor"
            label-right="Excelent"
          />
        </template>
      </template>
    </div>

    <!-- CREDIBILITY -->
    <div class="flex flex-col gap-6">
      <Divider
        align="left"
        type="solid"
        class="m-0"
        :pt="{
          content: {
            class: 'bg-white'
          }
        }"
      >
        <span class="text-neutral-60 text-14 font-bold uppercase">Credibility</span>
      </Divider>

      <template
        v-for="field in activityCredibilityRatingsFields"
        :key="field.title"
      >
        <RatingScale
          v-if="activityCredibilityRatings && field.value in activityCredibilityRatings"
          v-model="activityCredibilityRatings[field.value]"
          :title="field.title"
          :options="field.options"
          :invalid="!!errors.activityCredibilityRatings && !activityCredibilityRatings[field.value]"
        />
      </template>

      <div class="flex flex-col gap-4">
        <label class="text-16 font-bold text-neutral-80">
          What did you like about this activity (Select all that apply)
        </label>

        <div class="flex flex-col gap-2">
          <label
            v-for="(item) in activityWhatDidYouLikeFields"
            :key="item.value"
            class="flex items-center gap-2"
          >
            <Checkbox
              v-if="activityWhatDidYouLike && item.value in activityWhatDidYouLike"
              v-model="activityWhatDidYouLike[item.value]"
              binary
              :invalid="!!errors.activityWhatDidYouLike"
              @update:model-value="activityWhatDidYouLikeProps.onBlur"
            />

            <span>
              {{item.label}}
            </span>
          </label>
        </div>

        <FormFieldError id="commitmentToChange-help">
          {{errors.activityWhatDidYouLike}}
        </FormFieldError>
      </div>

      <div class="flex flex-col gap-2">
        <FormFieldLabel for="activityMostUsefulInformation">
          What was the most useful information you gained from this activity?
        </FormFieldLabel>

        <Textarea
          id="activityMostUsefulInformation"
          v-model="activityMostUsefulInformation"
          class="resize-y"
          :invalid="!!errors.activityMostUsefulInformation"
          :disabled="loading"
          aria-describedby="activityMostUsefulInformation-help"
          rows="5"
          @blur="activityMostUsefulInformationProps.onBlur"
        />

        <FormFieldError id="activityMostUsefulInformation-help">
          {{errors.activityMostUsefulInformation}}
        </FormFieldError>
      </div>
    </div>

    <!-- GENERAL -->
    <div class="flex flex-col gap-6">
      <Divider
        align="left"
        type="solid"
        class="m-0"
        :pt="{
          content: {
            class: 'bg-white'
          }
        }"
      >
        <span class="text-neutral-60 text-14 font-bold uppercase">General</span>
      </Divider>

      <div class="flex flex-col gap-2">
        <FormFieldLabel for="activityMostUsefulInformation">
          Suggested Topics for future activities
        </FormFieldLabel>

        <Textarea
          id="suggestedTopicsForFuture"
          v-model="suggestedTopicsForFuture"
          :invalid="!!errors.suggestedTopicsForFuture"
          :disabled="loading"
          aria-describedby="suggestedTopicsForFuture-help"
          class="resize-y"
          rows="5"
          @blur="suggestedTopicsForFutureProps.onBlur"
        />

        <FormFieldError id="suggestedTopicsForFuture-help">
          {{errors.suggestedTopicsForFuture}}
        </FormFieldError>
      </div>

      <div class="flex flex-col gap-2">
        <FormFieldLabel for="activityMostUsefulInformation">
          General comments/suggestions
        </FormFieldLabel>

        <Textarea
          id="generalCommentsAndSuggestions"
          v-model="generalCommentsAndSuggestions"
          :invalid="!!errors.generalCommentsAndSuggestions"
          :disabled="loading"
          aria-describedby="generalCommentsAndSuggestions-help"
          class="resize-y"
          rows="5"
          @blur="generalCommentsAndSuggestionsProps.onBlur"
        />

        <FormFieldError id="generalCommentsAndSuggestions-help">
          {{errors.generalCommentsAndSuggestions}}
        </FormFieldError>
      </div>
    </div>

    <Divider class="m-0" />

    <!-- AGREEMENT -->
    <div class="flex flex-col gap-3">
      <div>
        <label class="flex gap-2 text-14 font-bold">
          <Checkbox
            v-model="userAgreedToDataSharing"
            binary
            :disabled="loading"
            @update:model-value="userAgreedToDataSharingProps.onBlur"
          />

          <span>
            I acknowledge that the data submitted with this form, including my email address, the date of submission,
            the name of the completed activity, and the quiz results, will be transmitted to the CME accrediting organization. *
          </span>
        </label>
        <FormFieldError id="generalCommentsAndSuggestions-help">
          {{errors.userAgreedToDataSharing}}
        </FormFieldError>
      </div>

      <div class="text-neutral-70 text-14 leading-150">
        AMA PRA Category 1 Credits™: CME Outfitters, 201 N King of Prussia Rd, Suite 370, Radnor, PA 19087.
      </div>
    </div>

    <Divider class="m-0" />

    <div class="flex flex-col gap-4">
      <!-- COURSE RATING -->
      <div class="flex flex-col items-center gap-2">
        <div class="text-16">
          Please rate the learning experience of this course.
        </div>
        <StarRating
          v-model="courseRating"
          :disabled="loading"
          @update:model-value="courseRatingProps.onBlur"
        />
        <FormFieldError id="generalCommentsAndSuggestions-help">
          {{errors.courseRating}}
        </FormFieldError>
      </div>

      <div class="flex justify-center mb-4">
        <Button
          :loading="loading"
          class="font-bold !text-22 flex gap-2 px-12 bg-blue-30 text-neutral-90 border-none rounded-full"
          type="submit"
          @click="onSubmit"
        >
          <FontAwesomeIcon
            v-if="loading"
            icon="fa-circle-notch"
            class="animate-spin"
          />
          <FontAwesomeIcon
            v-else
            icon="fa-file-certificate"
          />
          <span>Claim certificate</span>
        </Button>
      </div>
    </div>
  </form>
</template>